/**
 * World Cup 2026 dates and countdown phase logic.
 *
 * Per the FIFA match schedule (released Feb 2024):
 *   Opening match — Thursday June 11, 2026, Estadio Azteca, Mexico City.
 *                   Local kickoff 12:00 CDT (UTC-5) → 17:00 UTC.
 *   Final         — Sunday July 19, 2026, MetLife Stadium, New Jersey.
 *                   Local kickoff 16:00 EDT (UTC-4) → 20:00 UTC.
 *
 * TODO(verify): FIFA can shift kickoff slots up to ~6 months out. Re-check
 * these constants against the official schedule a month before the
 * tournament and bump if needed. The ISO strings make it a one-line edit.
 */
export const KICKOFF_AT_ISO = "2026-06-11T17:00:00.000Z";
export const FINAL_AT_ISO = "2026-07-19T20:00:00.000Z";

export const KICKOFF_AT_MS = Date.parse(KICKOFF_AT_ISO);
export const FINAL_AT_MS = Date.parse(FINAL_AT_ISO);

export type CountdownPhase = "pre" | "during" | "post";

export interface CountdownState {
  phase: CountdownPhase;
  /** Milliseconds remaining until the next event. 0 in "post". */
  remainingMs: number;
  /** Label for the event we're counting down to. */
  label: "Kickoff" | "Final" | null;
}

export function getCountdown(now: number = Date.now()): CountdownState {
  if (now < KICKOFF_AT_MS) {
    return { phase: "pre", remainingMs: KICKOFF_AT_MS - now, label: "Kickoff" };
  }
  if (now < FINAL_AT_MS) {
    return { phase: "during", remainingMs: FINAL_AT_MS - now, label: "Final" };
  }
  return { phase: "post", remainingMs: 0, label: null };
}

export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export function formatRemaining(ms: number): CountdownParts {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return { days, hours, minutes, seconds };
}

/**
 * Human-readable countdown string. Drops the trailing unit at each tier so
 * we don't show jittery precision the user doesn't need:
 *   days > 0 → "25d 08h"     (no minutes — irrelevant at this distance)
 *   hours > 0 → "5h 23m"     (no seconds — irrelevant in the last day)
 *   else     → "23m 45s"     (last hour — second precision matters)
 *
 * Padding keeps the digit width stable across ticks so the strip doesn't
 * shimmer between renders.
 */
export function formatCountdownString(parts: CountdownParts): string {
  const { days, hours, minutes, seconds } = parts;
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (days > 0) {
    return `${days}d ${pad(hours)}h`;
  }
  if (hours > 0) {
    return `${hours}h ${pad(minutes)}m`;
  }
  return `${minutes}m ${pad(seconds)}s`;
}
